import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import {
  cropRectToWebp,
  IMAGE_WEBP_MIME,
  readFileAsDataURL,
  type CropAreaPixels,
} from '../../utils/imageCompress';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

export type LandingImageCropPreset = {
  /** 크롭 비율 (가로/세로) */
  aspect: number;
  outputWidth: number;
  outputHeight: number;
  /** 모달 제목 */
  title: string;
  /** 비율 안내 문구 */
  ratioHint: string;
};

export const LANDING_IMAGE_PRESETS = {
  hero: {
    aspect: 16 / 9,
    outputWidth: 1920,
    outputHeight: 1080,
    title: '히어로 이미지 등록',
    ratioHint: '히어로 캐러셀 비율(16:9)',
  },
  feature: {
    aspect: 16 / 9,
    outputWidth: 1600,
    outputHeight: 900,
    title: '특징 이미지 등록',
    ratioHint: '특징 섹션 비율(16:9)',
  },
  partner: {
    aspect: 1,
    outputWidth: 512,
    outputHeight: 512,
    title: '협업기관 로고 등록',
    ratioHint: '협업기관 로고 비율(1:1 정사각)',
  },
} as const satisfies Record<string, LandingImageCropPreset>;

export type LandingImageCropModalProps = {
  open: boolean;
  preset: LandingImageCropPreset;
  onClose: () => void;
  /** 크롭·WebP 변환된 파일 */
  onConfirm: (file: File) => void;
};

const LandingImageCropModal: React.FC<LandingImageCropModalProps> = ({
  open,
  preset,
  onClose,
  onConfirm,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setRawImageSrc('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsSaving(false);
      setError('');
      cropPixelsRef.current = null;
    }
  }, [open]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    const dataUrl = await readFileAsDataURL(file);
    setRawImageSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
  }, []);

  useEffect(() => {
    if (!open || rawImageSrc) return;
    const onPaste = (e: ClipboardEvent) => {
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', {
              type: blob.type || 'image/png',
            });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, rawImageSrc, handleFile]);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleSaveClick = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const blob = await cropRectToWebp(
        rawImageSrc,
        cropPixelsRef.current,
        preset.outputWidth,
        preset.outputHeight,
        0.8,
      );
      const file = new File([blob], `landing-${Date.now()}.webp`, {
        type: IMAGE_WEBP_MIME,
      });
      onConfirm(file);
      onClose();
    } catch (e) {
      console.error(e);
      setError('이미지 저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="cp-member-photo-upload-overlay cp-apn-cover-upload-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      role="presentation"
    >
      <div
        className="cp-member-photo-upload-modal cp-apn-cover-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label={preset.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-member-photo-upload-header">
          <h3>{preset.title}</h3>
          <button type="button" className="cp-member-photo-upload-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        {error ? (
          <p className="cp-member-photo-upload-error" role="alert">
            {error}
          </p>
        ) : null}

        {!rawImageSrc ? (
          <div
            className={`cp-member-photo-upload-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <span className="cp-member-photo-upload-drop-plus" aria-hidden>
              +
            </span>
            <p className="cp-member-photo-upload-drop-text">
              이미지를 드래그·클릭하거나 Ctrl+V로 붙여넣기
            </p>
            <p className="cp-member-photo-upload-drop-hint">
              {preset.ratioHint}에 맞춰 자를 수 있습니다 · JPG/PNG/WebP · 10MB 이하
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="cp-member-photo-upload-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleFile(file);
              }}
            />
          </div>
        ) : (
          <div className="cp-member-photo-upload-crop-wrap">
            <div className="cp-member-photo-upload-crop cp-apn-cover-upload-crop">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={preset.aspect}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, pixels) => {
                  cropPixelsRef.current = pixels;
                }}
              />
            </div>
            <label className="cp-member-photo-upload-zoom">
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
            <div className="cp-member-photo-upload-footer">
              <button
                type="button"
                className="cp-member-photo-upload-btn is-cancel"
                onClick={() => {
                  setRawImageSrc('');
                  cropPixelsRef.current = null;
                }}
                disabled={isSaving}
              >
                다시 선택
              </button>
              <button
                type="button"
                className="cp-member-photo-upload-btn is-save"
                onClick={() => void handleSaveClick()}
                disabled={isSaving}
              >
                {isSaving ? '처리 중…' : '등록'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default LandingImageCropModal;
